/**
 * Top Bar Navigation Configuration
 *
 * Defines the module and screen structure for the Mega Menu
 * used in the Top Bar navigation layout. All routes map to
 * existing entries in app.routes.ts.
 */

import {
  EmcsDestinationTypeCode,
  EmcsOriginTypeCode,
} from '../pages/emcs/utils/emcs-codebook';

export interface TopBarModule {
  /** Unique module key (matches sidebar/footer module ids) */
  key: string;
  /** Display name for the module */
  name: string;
  /** Translation key for i18n */
  translationKey: string;
  /** Icon identifier used by the icon component library */
  icon: string;
  /** Brand colour for the module icon circle */
  color: string;
  /** Default route when clicking the module header */
  route: string;
  /** Sub-screens within this module */
  screens: TopBarScreen[];
}

export interface TopBarScreen {
  /** Unique screen key within the module */
  key: string;
  /** Display name */
  name: string;
  /** Translation key for i18n */
  translationKey: string;
  /** Navigation route */
  route: string;
  /** Optional query parameters for tab navigation */
  queryParams?: Record<string, string>;
}

export interface PinnedScreen {
  /** Module key */
  moduleKey: string;
  /** Screen key */
  screenKey: string;
  /** Direct navigation route */
  route: string;
  /** Optional query parameters */
  queryParams?: Record<string, string>;
}

export const TOP_BAR_MODULES: TopBarModule[] = [
  {
    key: 'import',
    name: 'Import',
    translationKey: 'import',
    icon: 'import',
    color: '#3b82f6',
    route: '/import',
    screens: [
      {
        key: 'shipments',
        name: 'Shipments',
        translationKey: 'shipments',
        route: '/import/shipments',
      },
      {
        key: 'declarations',
        name: 'Declarations',
        translationKey: 'declarations',
        route: '/import/declarations',
      },
    ],
  },
  {
    key: 'export',
    name: 'Export',
    translationKey: 'export',
    icon: 'export',
    color: '#06b6d4',
    route: '/export',
    screens: [
      {
        key: 'shipments',
        name: 'Shipments',
        translationKey: 'shipments',
        route: '/export/shipments',
      },
      {
        key: 'declarations',
        name: 'Declarations',
        translationKey: 'declarations',
        route: '/export/declarations',
      },
    ],
  },
  {
    key: 'transit',
    name: 'NCTS',
    translationKey: 'transitNcts',
    icon: 'transit',
    color: '#f59e0b',
    route: '/transit-declarations/transit-arrival',
    screens: [
      {
        key: 'transit-arrival',
        name: 'Transit Arrival',
        translationKey: 'transitArrival',
        route: '/transit-declarations/transit-arrival',
      },
      {
        key: 'transit-departure',
        name: 'Transit Departure',
        translationKey: 'transitDeparture',
        route: '/transit-declarations/transit-departure',
      },
    ],
  },
  {
    key: 'emcs',
    name: 'EMCS',
    translationKey: 'emcs',
    icon: 'emcs',
    color: '#f97316',
    route: '/emcs',
    screens: [
      {
        key: 'emcs-list',
        name: 'EU Movements',
        translationKey: 'euMovements',
        route: '/emcs',
      },
      {
        key: 'emcs-import',
        name: 'Import',
        translationKey: 'import',
        route: '/emcs',
        queryParams: {
          specificValueFields: JSON.stringify({
            originTypeCode: EmcsOriginTypeCode.Import,
          }),
        },
      },
      {
        key: 'emcs-export',
        name: 'Export',
        translationKey: 'export',
        route: '/emcs',
        queryParams: {
          specificValueFields: JSON.stringify({
            destinationTypeCode: EmcsDestinationTypeCode.Export,
          }),
        },
      },
    ],
  },
  {
    key: 'rto',
    name: 'PGTS',
    translationKey: 'pgts',
    icon: 'warehouse',
    color: '#10b981',
    route: '/rto/shipments',
    screens: [
      {
        key: 'rto-all',
        name: 'All PGTS Shipments',
        translationKey: 'allPgtsShipments',
        route: '/rto/shipments',
      },
      {
        key: 'rto-import',
        name: 'Import Shipments',
        translationKey: 'importShipments',
        route: '/rto/shipments/import',
      },
      {
        key: 'rto-export',
        name: 'Export Shipments',
        translationKey: 'exportShipments',
        route: '/rto/shipments/export',
      },
    ],
  },
  {
    key: 'tasks',
    name: 'Tasks',
    translationKey: 'tasks',
    icon: 'tasks',
    color: '#14b8a6',
    route: '/tickets/tasks',
    screens: [
      {
        key: 'tasks',
        name: 'Tasks',
        translationKey: 'tasks',
        route: '/tickets/tasks',
      },
    ],
  },
  {
    key: 'commodity-classifier',
    name: 'Commodity Classifier',
    translationKey: 'commodityClassifier',
    icon: 'commodity-classifier',
    color: '#0ea5e9',
    route: '/commodity-classifier',
    screens: [
      {
        key: 'classifier',
        name: 'Commodity Classifier',
        translationKey: 'commodityClassifier',
        route: '/commodity-classifier',
      },
    ],
  },
  {
    key: 'settings',
    name: 'Global Modules',
    translationKey: 'globalModules',
    icon: 'globe',
    color: '#6366f1',
    route: '/settings/brokers',
    screens: [
      {
        key: 'brokers',
        name: 'Brokers',
        translationKey: 'brokers',
        route: '/settings/brokers',
      },
      {
        key: 'clients',
        name: 'Clients',
        translationKey: 'clients',
        route: '/settings/clients',
      },
      {
        key: 'parties',
        name: 'Parties',
        translationKey: 'parties',
        route: '/settings/parties',
      },
      {
        key: 'declaration-templates',
        name: 'Templates',
        translationKey: 'templates',
        route: '/settings/declaration-templates',
      },
      {
        key: 'roles',
        name: 'Roles',
        translationKey: 'roles',
        route: '/settings/roles',
      },
      {
        key: 'users',
        name: 'Users',
        translationKey: 'users',
        route: '/settings/users',
      },
    ],
  },
  {
    key: 'dashboard',
    name: 'Dashboard',
    translationKey: 'dashboard',
    icon: 'graph',
    color: '#8b5cf6',
    route: '/dashboard/shipments',
    screens: [
      {
        key: 'shipments',
        name: 'Shipments',
        translationKey: 'shipments',
        route: '/dashboard/shipments',
      },
      {
        key: 'declarations',
        name: 'Declarations',
        translationKey: 'declarations',
        route: '/dashboard/declarations',
      },
      {
        key: 'transit',
        name: 'NCTS',
        translationKey: 'transitNcts',
        route: '/dashboard/transit',
      },
      {
        key: 'emcs',
        name: 'EMCS',
        translationKey: 'emcs',
        route: '/dashboard/emcs',
      },
      {
        key: 'rto',
        name: 'PGTS',
        translationKey: 'pgts',
        route: '/dashboard/rto',
      },
      {
        key: 'tasks',
        name: 'Tasks',
        translationKey: 'tasks',
        route: '/dashboard/tickets',
      },
      {
        key: 'global-modules',
        name: 'Global Modules',
        translationKey: 'globalModules',
        route: '/dashboard/settings',
      },
    ],
  },
];
